import { useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { Icon } from './Icon'
import { IconButton } from './Button'
import './ui.css'

/**
 * A register section heading: a mono label with a hairline running to the edge.
 * It marks a division of the register, which is information, not decoration.
 */
export function Eyebrow({
  children,
  action,
  to,
  onAction,
}: {
  children: ReactNode
  action?: string
  to?: string
  onAction?: () => void
}) {
  return (
    <div className="eyebrow">
      <span className="u-plate">{children}</span>
      {action && to && (
        <Link to={to} className="eyebrow__action">
          {action}
        </Link>
      )}
      {action && !to && (
        <button type="button" className="eyebrow__action" onClick={onAction}>
          {action}
        </button>
      )}
    </div>
  )
}

export interface Stat {
  value: string | number
  label: string
}

/** Hairline-separated readings across the top of the register. */
export function StatStrip({ stats }: { stats: Stat[] }) {
  return (
    <div className={'stats' + (stats.length <= 2 ? ' stats--pair' : stats.length >= 4 ? ' stats--quad' : '')}>
      {stats.map((s) => (
        <div className="stats__cell" key={s.label}>
          <span className="stats__value">{s.value}</span>
          <span className="stats__label">{s.label}</span>
        </div>
      ))}
    </div>
  )
}

export function TopBar({
  title,
  back = true,
  onBack,
  action,
  transparent,
}: {
  title?: string
  back?: boolean
  onBack?: () => void
  action?: ReactNode
  transparent?: boolean
}) {
  const navigate = useNavigate()
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    if (transparent) return
    const onScroll = () => setScrolled(window.scrollY > 4)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [transparent])

  return (
    <div
      className={clsx('topbar', transparent && 'topbar--transparent')}
      data-scrolled={scrolled}
    >
      {back && (
        <IconButton
          name="back"
          label="Volver"
          onPhoto={transparent}
          onClick={() => (onBack ? onBack() : navigate(-1))}
        />
      )}
      {title && !transparent && <h1 className="topbar__title">{title}</h1>}
      {(!title || transparent) && <span className="spacer" />}
      {action}
    </div>
  )
}

export function RowLink({
  to,
  onClick,
  lead,
  title,
  sub,
  trail,
  chevron = true,
}: {
  to?: string
  onClick?: () => void
  lead?: ReactNode
  title: ReactNode
  sub?: ReactNode
  trail?: ReactNode
  chevron?: boolean
}) {
  const content = (
    <>
      {lead && <span className="row-link__lead">{lead}</span>}
      <span className="row-link__main">
        <span className="row-link__title">{title}</span>
        {sub && <span className="row-link__sub">{sub}</span>}
      </span>
      {trail}
      {chevron && (to || onClick) && (
        <span className="row-link__chev">
          <Icon name="chevron-right" size={17} />
        </span>
      )}
    </>
  )
  if (to) {
    return (
      <Link to={to} className="row-link">
        {content}
      </Link>
    )
  }
  if (onClick) {
    return (
      <button type="button" className="row-link" onClick={onClick}>
        {content}
      </button>
    )
  }
  return <div className="row-link">{content}</div>
}

/** A ledger row: a key on the left, a measured value on the right. */
export function LedgerRow({
  label,
  children,
  onClick,
}: {
  label: string
  children: ReactNode
  onClick?: () => void
}) {
  const inner = (
    <>
      <span className="ledger__key u-body-sm">{label}</span>
      <span className="ledger__val u-body-sm">{children}</span>
    </>
  )
  if (onClick) {
    return (
      <button type="button" className="ledger__row" onClick={onClick}>
        {inner}
      </button>
    )
  }
  return <div className="ledger__row">{inner}</div>
}
